/**
 * ArtifactRenderer Component
 *
 * Renderiza o conteúdo do artefato baseado no tipo
 * - Type-safe com constantes centralizadas
 * - Sem console.logs
 * - Base64 decode otimizado
 */

import React, { useEffect, useRef, useState, memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Document, Page, pdfjs } from 'react-pdf';
import { renderAsync } from 'docx-preview';
import { ARTIFACT_TYPES } from '../constants';
import type { Artifact } from '../utils';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// =============================================================================
// TYPES
// =============================================================================

interface ArtifactRendererProps {
  artifact: Artifact;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Decodifica base64 para ArrayBuffer de forma otimizada
 */
const decodeBase64ToArrayBuffer = (base64: string): ArrayBuffer => {
  let data = base64;
  if (data.includes('base64,')) {
    data = data.split('base64,')[1];
  }
  const binaryString = atob(data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Tenta parsear JSON de forma segura
 */
const safeJsonParse = <T,>(content: string): T | null => {
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Classes de estilização para renderização de Markdown
 * Extração para constante melhora manutenibilidade e legibilidade
 */
const MARKDOWN_STYLES = `
  prose prose-invert prose-sm max-w-none p-4
  [&_p]:mb-3 [&_p:last-child]:mb-0
  [&_strong]:text-[#F4CC04] [&_strong]:font-bold
  [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-3 [&_h1]:leading-tight
  [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-3.5 [&_h2]:mb-2.5 [&_h2]:leading-tight
  [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:leading-tight
  [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mt-2.5 [&_h4]:mb-2
  [&_h5]:text-sm [&_h5]:font-semibold [&_h5]:mt-2 [&_h5]:mb-1.5
  [&_h6]:text-sm [&_h6]:font-medium [&_h6]:mt-2 [&_h6]:mb-1.5
  [&_ul]:pl-6 [&_ul]:mb-3 [&_ul]:list-disc
  [&_ol]:pl-6 [&_ol]:mb-3 [&_ol]:list-decimal
  [&_li]:mb-1
  [&_pre]:bg-black/20 [&_pre]:p-3 [&_pre]:rounded-md [&_pre]:overflow-x-auto [&_pre]:my-3
  [&_code]:font-mono [&_code]:bg-black/20 [&_code]:px-1 [&_code]:rounded [&_code]:text-sm
  [&_blockquote]:border-l-4 [&_blockquote]:border-[#F4CC04] [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:my-3
  [&_div:has(>table)]:overflow-x-auto [&_div:has(>table)]:my-4
  [&_table]:border-collapse [&_table]:w-full [&_table]:min-w-max
  [&_th]:border [&_th]:border-white/20 [&_th]:p-2 [&_th]:bg-black/20 [&_th]:font-semibold [&_th]:whitespace-nowrap
  [&_td]:border [&_td]:border-white/20 [&_td]:p-2
  [&_hr]:border-white/20 [&_hr]:my-4
  [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md [&_img]:my-3
`.trim();

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Renderiza links como texto simples por segurança
 * Remove vetores de ataque via javascript:, data:, etc.
 */
const PlainTextLink: React.FC<React.ComponentProps<'a'>> = memo(({ children }) => (
  <span className="text-[#F4CC04] font-medium">{children}</span>
));
PlainTextLink.displayName = 'PlainTextLink';

/**
 * Renderiza Markdown com estilização completa
 * Links são renderizados como texto simples por segurança
 */
const MarkdownRenderer: React.FC<{ content: string }> = memo(({ content }) => (
  <div className={MARKDOWN_STYLES}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: PlainTextLink,
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
));
MarkdownRenderer.displayName = 'MarkdownRenderer';

/**
 * Renderiza HTML em iframe sandboxed
 */
const HtmlRenderer: React.FC<{ content: string; title: string }> = memo(({ content, title }) => (
  <iframe
    className="w-full h-full min-h-[500px] bg-white"
    srcDoc={content}
    sandbox="allow-scripts"
    title={title}
  />
));
HtmlRenderer.displayName = 'HtmlRenderer';

/**
 * Renderiza código
 */
const CodeRenderer: React.FC<{ content: string; language?: string }> = memo(
  ({ content, language }) => (
    <pre className="p-4 bg-[#1a1a1a] overflow-auto">
      <code className={language ? `language-${language}` : ''}>{content}</code>
    </pre>
  )
);
CodeRenderer.displayName = 'CodeRenderer';

/**
 * Verifica se o conteúdo é HTML
 */
const isHtmlContent = (content: string): boolean => {
  const trimmed = content.trim().toLowerCase();
  return (
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<head') ||
    trimmed.startsWith('<body') ||
    (trimmed.includes('<html') && trimmed.includes('</html>'))
  );
};

/**
 * Renderiza PDF - suporta tanto HTML (para preview) quanto base64 (PDF binário)
 */
const PdfRenderer: React.FC<{ content: string }> = memo(({ content }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);

  // Detecta se é HTML ou base64
  const contentIsHtml = useMemo(() => isHtmlContent(content), [content]);

  const pdfData = useMemo(() => {
    if (contentIsHtml) return null; // Não precisa de pdfData para HTML
    if (content.startsWith('data:application/pdf;base64,')) {
      return content;
    }
    if (content.includes('base64,')) {
      return `data:application/pdf;base64,${content.split('base64,')[1]}`;
    }
    return `data:application/pdf;base64,${content}`;
  }, [content, contentIsHtml]);

  const handleLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
    setPageNumber(1);
  };

  // Se é HTML, renderiza como preview (será convertido para PDF no download)
  if (contentIsHtml) {
    return (
      <div className="flex flex-col h-full">
        {/* Info banner */}
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border-b border-blue-500/20 text-blue-400 text-sm">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span>Preview do documento - clique em Download para obter o PDF</span>
        </div>

        {/* HTML Preview */}
        <iframe
          className="flex-1 w-full bg-white"
          srcDoc={content}
          sandbox="allow-same-origin"
          title="PDF Preview"
        />
      </div>
    );
  }

  // Se é base64, renderiza como PDF binário
  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="flex items-center gap-3 p-3 bg-[#1a1a1a] border-b border-white/10">
        <button
          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          disabled={pageNumber <= 1}
          className="px-3 py-1.5 text-sm bg-white/10 rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Anterior
        </button>
        <span className="text-sm text-gray-400">
          Página {pageNumber} de {numPages || '?'}
        </span>
        <button
          onClick={() => setPageNumber((p) => Math.min(numPages || p, p + 1))}
          disabled={pageNumber >= (numPages || 0)}
          className="px-3 py-1.5 text-sm bg-white/10 rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Próxima
        </button>
      </div>

      {/* PDF Document */}
      <div className="flex-1 overflow-auto p-4 flex justify-center">
        <Document
          file={pdfData}
          onLoadSuccess={handleLoadSuccess}
          loading={<div className="text-gray-400">Carregando PDF...</div>}
          error={<div className="text-red-400">Erro ao carregar PDF</div>}
        >
          <Page
            pageNumber={pageNumber}
            width={Math.min(600, window.innerWidth - 80)}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
    </div>
  );
});
PdfRenderer.displayName = 'PdfRenderer';

/**
 * Renderiza DOCX binário
 */
const DocxRenderer: React.FC<{ content: string }> = memo(({ content }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const renderDocx = async () => {
      try {
        const arrayBuffer = decodeBase64ToArrayBuffer(content);
        const blob = new Blob([arrayBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });

        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          await renderAsync(blob, containerRef.current, undefined, {
            className: 'docx-wrapper',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            experimental: true,
            renderHeaders: true,
            renderFooters: true,
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao renderizar DOCX');
      }
    };

    renderDocx();
  }, [content]);

  if (error) {
    return (
      <div className="p-4 text-red-400">
        Erro ao renderizar documento: {error}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="docx-container p-4 bg-white text-black min-h-[500px]"
    />
  );
});
DocxRenderer.displayName = 'DocxRenderer';

/**
 * Tipos para DOCX estruturado
 */
interface DocxCell {
  text: string;
  bold?: boolean;
}

interface DocxTableRow {
  cells: DocxCell[];
}

interface DocxParagraph {
  type: 'paragraph';
  text: string;
  heading?: 'TITLE' | 'HEADING_1' | 'HEADING_2' | 'HEADING_3';
  bold?: boolean;
  italics?: boolean;
  alignment?: 'center' | 'left' | 'right';
  size?: number;
  color?: string;
}

interface DocxTable {
  type: 'table';
  rows: DocxTableRow[];
}

type DocxElement = DocxParagraph | DocxTable;

interface DocxSection {
  children: DocxElement[];
}

interface DocxStructure {
  sections: DocxSection[];
}

/**
 * Renderiza um parágrafo do DOCX estruturado
 */
const DocxParagraphRenderer: React.FC<{ element: DocxParagraph }> = memo(({ element }) => {
  const { text, heading, bold, italics, alignment, color } = element;

  // Classes base
  let className = 'mb-2 ';

  // Alinhamento
  if (alignment === 'center') className += 'text-center ';
  else if (alignment === 'right') className += 'text-right ';

  // Headings
  if (heading === 'TITLE') {
    return (
      <h1
        className={`text-2xl font-bold mb-4 ${alignment === 'center' ? 'text-center' : ''}`}
        style={{ color: color ? `#${color}` : undefined }}
      >
        {text}
      </h1>
    );
  }

  if (heading === 'HEADING_1') {
    return (
      <h2
        className={`text-xl font-bold mt-6 mb-3 ${alignment === 'center' ? 'text-center' : ''}`}
        style={{ color: color ? `#${color}` : undefined }}
      >
        {text}
      </h2>
    );
  }

  if (heading === 'HEADING_2') {
    return (
      <h3
        className={`text-lg font-semibold mt-4 mb-2 ${alignment === 'center' ? 'text-center' : ''}`}
        style={{ color: color ? `#${color}` : undefined }}
      >
        {text}
      </h3>
    );
  }

  if (heading === 'HEADING_3') {
    return (
      <h4
        className={`text-base font-semibold mt-3 mb-2`}
        style={{ color: color ? `#${color}` : undefined }}
      >
        {text}
      </h4>
    );
  }

  // Parágrafo vazio
  if (!text || text.trim() === '') {
    return <div className="h-2" />;
  }

  // Parágrafo normal
  return (
    <p
      className={className}
      style={{ color: color ? `#${color}` : undefined }}
    >
      {bold ? <strong>{italics ? <em>{text}</em> : text}</strong> : italics ? <em>{text}</em> : text}
    </p>
  );
});
DocxParagraphRenderer.displayName = 'DocxParagraphRenderer';

/**
 * Renderiza uma tabela do DOCX estruturado
 */
const DocxTableRenderer: React.FC<{ element: DocxTable }> = memo(({ element }) => {
  const { rows } = element;

  if (!rows || rows.length === 0) return null;

  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full border-collapse text-sm">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={rowIndex === 0 ? 'bg-white/10' : rowIndex % 2 === 0 ? 'bg-white/5' : ''}
            >
              {row.cells.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`border border-white/20 px-3 py-2 ${cell.bold ? 'font-semibold' : ''}`}
                >
                  {cell.text}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});
DocxTableRenderer.displayName = 'DocxTableRenderer';

/**
 * Tenta reparar JSON truncado adicionando fechamentos
 * Estratégias:
 * 1. Encontra o último elemento completo (}, ou }])
 * 2. Remove strings incompletas
 * 3. Fecha brackets e braces faltantes
 */
const tryRepairJson = (content: string): string => {
  let repaired = content.trim();

  // Estratégia 1: Encontrar o último elemento JSON completo
  // Procura padrões que indicam fim de um objeto/elemento válido
  const patterns = [
    /\}\s*\]\s*\}\s*\]\s*\}$/,  // }]}]} - fim de sections
    /\}\s*\]\s*\}$/,            // }]} - fim de children dentro de section
    /\}\s*,\s*$/,               // }, - elemento seguido de vírgula
    /\}\s*\]$/,                 // }] - fim de array
    /"\s*\}$/,                  // "} - fim de string em objeto
    /\d\s*\}$/,                 // N} - número seguido de fechamento
    /true\s*\}$/,               // true} - boolean
    /false\s*\}$/,              // false}
    /null\s*\}$/,               // null}
  ];

  // Verifica se já termina com padrão válido
  let needsRepair = true;
  for (const pattern of patterns) {
    if (pattern.test(repaired)) {
      needsRepair = false;
      break;
    }
  }

  if (needsRepair) {
    // Estratégia 2: Encontrar último elemento completo
    // Procura o último "}" que provavelmente fecha um objeto completo

    // Primeiro, tenta encontrar o último "}," ou "}]"
    const lastValidEndings = [
      repaired.lastIndexOf('}]'),
      repaired.lastIndexOf('},'),
      repaired.lastIndexOf('"}'),
    ];

    let cutPoint = Math.max(...lastValidEndings);

    if (cutPoint > 0 && cutPoint < repaired.length - 2) {
      // Determina quantos caracteres manter após o cutPoint
      const endChar = repaired[cutPoint + 1];
      if (endChar === ']' || endChar === ',') {
        repaired = repaired.substring(0, cutPoint + 2);
      } else if (endChar === '}') {
        repaired = repaired.substring(0, cutPoint + 2);
      }
    }

    // Remove vírgula trailing se houver
    repaired = repaired.replace(/,\s*$/, '');
  }

  // Estratégia 3: Contar e fechar brackets/braces
  const openBrackets = (repaired.match(/\[/g) || []).length;
  const closeBrackets = (repaired.match(/\]/g) || []).length;
  const openBraces = (repaired.match(/\{/g) || []).length;
  const closeBraces = (repaired.match(/\}/g) || []).length;

  // Adiciona fechamentos faltantes na ordem correta
  // Para um JSON de DOCX estruturado: {sections: [{children: [...]}]}
  // A ordem de fechamento deve ser: } ] } ] }

  const missingBrackets = openBrackets - closeBrackets;
  const missingBraces = openBraces - closeBraces;

  // Intercala fechamentos (aproximação para estrutura aninhada)
  let closings = '';
  let remainingBrackets = missingBrackets;
  let remainingBraces = missingBraces;

  // Padrão comum: ]}]}
  while (remainingBrackets > 0 || remainingBraces > 0) {
    if (remainingBrackets > 0) {
      closings += ']';
      remainingBrackets--;
    }
    if (remainingBraces > 0) {
      closings += '}';
      remainingBraces--;
    }
  }

  return repaired + closings;
};

/**
 * Renderiza DOCX estruturado (JSON)
 */
const DocxStructuredRenderer: React.FC<{ content: string; title: string }> = memo(
  ({ content }) => {
    // Se não é JSON, usa o renderer de DOCX binário
    if (!content.trim().startsWith('{')) {
      return <DocxRenderer content={content} />;
    }

    // Tenta parsear o JSON (primeiro tentativa normal, depois com reparo)
    let data = safeJsonParse<DocxStructure>(content);
    let wasRepaired = false;

    if (!data || !data.sections) {
      // Tenta reparar JSON truncado
      const repairedContent = tryRepairJson(content);
      data = safeJsonParse<DocxStructure>(repairedContent);
      wasRepaired = true;
    }

    if (!data || !data.sections) {
      // Se ainda falhar, mostra mensagem de erro com preview do conteúdo
      return (
        <div className="p-4 bg-[#1a1a1a] overflow-auto">
          <div className="text-yellow-400 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>Documento incompleto ou corrompido</span>
          </div>
          <pre className="text-sm text-gray-400 whitespace-pre-wrap break-words">
            {content.substring(0, 2000)}
            {content.length > 2000 && '...'}
          </pre>
        </div>
      );
    }

    return (
      <div className="p-6 bg-white text-gray-900 min-h-[500px] overflow-auto">
        {wasRepaired && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>Documento parcialmente recuperado - pode estar incompleto</span>
          </div>
        )}
        {data.sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-6">
            {section.children?.map((element, elementIndex) => {
              if (element.type === 'table') {
                return <DocxTableRenderer key={elementIndex} element={element as DocxTable} />;
              }
              return <DocxParagraphRenderer key={elementIndex} element={element as DocxParagraph} />;
            })}
          </div>
        ))}
      </div>
    );
  }
);
DocxStructuredRenderer.displayName = 'DocxStructuredRenderer';

/**
 * Renderiza componentes de impostos (Tax)
 * Por enquanto renderiza como JSON formatado
 */
const TaxRenderer: React.FC<{ content: string; type: string }> = memo(({ content }) => {
  const data = safeJsonParse(content);

  if (!data) {
    return <div className="p-4 text-red-400">Erro ao carregar dados</div>;
  }

  // Renderiza como JSON formatado por enquanto
  // Os componentes específicos (TaxComparisonTable, etc) podem ser importados depois
  return (
    <pre className="p-4 bg-[#1a1a1a] overflow-auto text-sm">
      <code>{JSON.stringify(data, null, 2)}</code>
    </pre>
  );
});
TaxRenderer.displayName = 'TaxRenderer';

/**
 * Fallback para tipos desconhecidos
 */
const FallbackRenderer: React.FC<{ content: string }> = memo(({ content }) => (
  <pre className="p-4 bg-[#1a1a1a] overflow-auto">
    <code>{content}</code>
  </pre>
));
FallbackRenderer.displayName = 'FallbackRenderer';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ArtifactRenderer: React.FC<ArtifactRendererProps> = memo(({ artifact }) => {
  switch (artifact.type) {
    case ARTIFACT_TYPES.MARKDOWN:
      return <MarkdownRenderer content={artifact.content} />;

    case ARTIFACT_TYPES.HTML:
      return <HtmlRenderer content={artifact.content} title={artifact.title} />;

    case ARTIFACT_TYPES.CODE:
    case 'code':
    case 'text/x-code':
      return <CodeRenderer content={artifact.content} language={artifact.language} />;

    case ARTIFACT_TYPES.PDF:
      return <PdfRenderer content={artifact.content} />;

    case ARTIFACT_TYPES.DOCX:
      return <DocxRenderer content={artifact.content} />;

    case ARTIFACT_TYPES.DOCX_STRUCTURED:
      return <DocxStructuredRenderer content={artifact.content} title={artifact.title} />;

    case ARTIFACT_TYPES.TAX_COMPARISON:
    case ARTIFACT_TYPES.TAX_CHART:
    case ARTIFACT_TYPES.TAX_BAR_CHART:
    case ARTIFACT_TYPES.TAX_SUMMARY:
      return <TaxRenderer content={artifact.content} type={artifact.type} />;

    case ARTIFACT_TYPES.PLAIN:
    case ARTIFACT_TYPES.CSV:
      return <FallbackRenderer content={artifact.content} />;

    default:
      return <FallbackRenderer content={artifact.content} />;
  }
});

ArtifactRenderer.displayName = 'ArtifactRenderer';

export default ArtifactRenderer;
